import { useState, useContext } from "react";
import moment from "moment";
import { AuthContext } from "../../../shared/context/auth-context";
import { useHttpClient } from "../../../shared/hooks/http-hook";
import { useLoading } from "../../../shared/context/LoadingContext";
import toast from "react-hot-toast";

/* Os estilos são os da ferramenta das entradas, de propósito: são o mesmo tipo
   de ecrã e devem parecer o mesmo. Duplicar a folha só faria as duas divergirem
   à primeira correção que alguém fizesse numa delas. */
import "../transferirPagamento/TransferirPagamento.scss";

// Extrai um ObjectId de uma string (ID puro ou link "/clientes/<id>").
const extrairId = (valor) => {
  if (!valor) return null;
  const trimmed = valor.trim();
  const match = trimmed.match(/\/clientes\/([a-f0-9]{24})/i);
  if (match) return match[1];
  if (/^[a-f0-9]{24}$/i.test(trimmed)) return trimmed;
  return null;
};

const formatarValor = (v) =>
  `${new Intl.NumberFormat("pt-PT").format(v || 0)} CVE`;

const descreverConta = (c) =>
  `Orçamento ${c.orcamento?.codigo ?? c.orcamento?.numero ?? "?"}` +
  ` — total ${formatarValor(c.total)}` +
  /* Dito na origem porque é ali que estas contas aparecem, e quem escolhe tem
     de perceber de onde está a tirar o dinheiro. */
  (c.aprovado ? "" : " (por aprovar)") +
  (c.interrompida ? " (interrompido)" : "");

function TransferirEntreContas() {
  const auth = useContext(AuthContext);
  const { sendRequest } = useHttpClient();
  const { startLoading, stopLoading } = useLoading();

  const [clienteInput, setClienteInput] = useState("");
  const [contas, setContas] = useState(null);

  const [contaOrigemSel, setContaOrigemSel] = useState("");
  const [contaDestinoSel, setContaDestinoSel] = useState("");
  const [parcelasSel, setParcelasSel] = useState([]);

  const [confirmando, setConfirmando] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [resultado, setResultado] = useState(null);

  const clienteId = extrairId(clienteInput);

  const origem = (contas || []).find((c) => c._id === contaOrigemSel);
  const destino = (contas || []).find((c) => c._id === contaDestinoSel);

  const escolhidas = (origem?.pagas || []).filter((p) =>
    parcelasSel.includes(p._id),
  );
  const totalSelecionado = escolhidas.reduce((s, p) => s + (p.valor || 0), 0);

  const alternarParcela = (id) =>
    setParcelasSel((atual) =>
      atual.includes(id) ? atual.filter((x) => x !== id) : [...atual, id],
    );

  const validacao = (() => {
    if (!origem || !destino || escolhidas.length === 0) return null;

    if (origem._id === destino._id) {
      return "A conta de origem e a de destino são a mesma.";
    }

    /* A SOMA, e não cada parcela de per si: duas de 5.000 cabem individualmente
       numa conta com 8.000 em aberto, mas juntas não. É a mesma verificação que
       o servidor faz — aqui só para avisar antes de tentar. */
    if (totalSelecionado > destino.totalEmAberto) {
      return `O total selecionado (${formatarValor(
        totalSelecionado,
      )}) excede o que está em aberto na conta de destino (${formatarValor(
        destino.totalEmAberto,
      )}).`;
    }

    /* A parcela que vai receber é a PRIMEIRA em aberto — a mesma regra das
       entradas. Se essa já tiver documento emitido, o servidor recusa, e é
       melhor dizê-lo agora do que depois de tudo preenchido. */
    const primeiraEmAberto = destino.emAberto?.[0];
    if (primeiraEmAberto?.temDocumento) {
      return `A parcela ${primeiraEmAberto.nrParcela} da conta de destino já tem documento eletrónico emitido.`;
    }

    return null;
  })();

  const podeExecutar =
    origem && destino && escolhidas.length > 0 && !validacao && !isLoading;

  const handleCarregarDados = async () => {
    if (!clienteId) {
      toast.error("Introduza um ID ou link de cliente válido.");
      return;
    }
    setContas(null);
    setContaOrigemSel("");
    setContaDestinoSel("");
    setParcelasSel([]);
    startLoading();
    try {
      const resposta = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/contascorrentes/transferenciaentrecontas/dados/${clienteId}`,
        "GET",
        null,
        { Authorization: "Bearer " + auth.token },
      );
      setContas(resposta.contas || []);
      if ((resposta.contas || []).length < 2) {
        toast("Este cliente não tem duas contas para transferir entre elas.");
      }
    } catch (err) {
      toast.error(err.message || "Erro ao carregar dados do cliente.");
    } finally {
      stopLoading();
    }
  };

  const handleExecutar = async () => {
    startLoading();
    setIsLoading(true);
    try {
      const resposta = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/contascorrentes/transferenciaentrecontas`,
        "POST",
        JSON.stringify({
          parcelaIds: parcelasSel,
          contaDestinoId: contaDestinoSel,
          utilizador: auth.userId,
        }),
        {
          "Content-Type": "application/json",
          Authorization: "Bearer " + auth.token,
        },
      );
      setResultado({ sucesso: true, dados: resposta });
      setConfirmando(false);
      toast.success(resposta?.message || "Pagamento transferido com sucesso.");
      handleCarregarDados();
    } catch (err) {
      setResultado({
        sucesso: false,
        mensagem: err.message || "Erro desconhecido.",
      });
      setConfirmando(false);
    } finally {
      setIsLoading(false);
      stopLoading();
    }
  };

  return (
    <div className="transferir-pagamento">
      <div className="transferir-pagamento__explicacao">
        <h3>O que faz este script?</h3>
        <p>
          Move o <strong>dinheiro de parcelas já pagas</strong> de uma conta para
          as parcelas em aberto de <strong>outra conta do mesmo cliente</strong>.
          Serve para quando se recebeu num orçamento o que era para outro.
        </p>
        <p>
          As parcelas de destino são tratadas como num recebimento normal:
          pagamento exato, divisão ou fusão. As parcelas de origem{" "}
          <strong>voltam a ficar por pagar</strong> — não são apagadas, porque a
          conta delas precisa das suas prestações. A fatura, o recibo e os
          registos de cancelamento seguem com o dinheiro.
        </p>
        <div className="transferir-pagamento__aviso">
          <span>⚠</span>
          <span>
            Operação sensível sobre dados financeiros e documentos fiscais.
            Confirme sempre o cliente, as parcelas e a conta de destino antes de
            executar.
          </span>
        </div>
      </div>

      {/* 1. Cliente */}
      <div className="transferir-pagamento__campo">
        <label className="transferir-pagamento__label">Cliente</label>
        <div className="transferir-pagamento__cliente-row">
          <input
            className="transferir-pagamento__input"
            type="text"
            placeholder="ID ou link da ficha do cliente"
            value={clienteInput}
            onChange={(e) => {
              setClienteInput(e.target.value);
              setContas(null);
              setResultado(null);
            }}
            disabled={isLoading}
          />
          <button
            className="transferir-pagamento__btn transferir-pagamento__btn--carregar"
            onClick={handleCarregarDados}
            disabled={isLoading || !clienteId}
          >
            Carregar
          </button>
        </div>
        {clienteId && (
          <span className="transferir-pagamento__id">ID: {clienteId}</span>
        )}
      </div>

      {contas && (
        <>
          {/* 2. Conta de origem */}
          <div className="transferir-pagamento__campo">
            <label className="transferir-pagamento__label">
              Conta de ORIGEM (de onde sai o dinheiro)
            </label>
            <select
              className="transferir-pagamento__input"
              value={contaOrigemSel}
              onChange={(e) => {
                setContaOrigemSel(e.target.value);
                setParcelasSel([]);
              }}
              disabled={isLoading}
            >
              <option value="">— escolher —</option>
              {contas
                .filter((c) => c.pagas.length > 0)
                .map((c) => (
                  <option key={c._id} value={c._id}>
                    {descreverConta(c)} · {c.pagas.length} parcela(s) paga(s)
                  </option>
                ))}
            </select>
          </div>

          {/* 3. Parcelas pagas da origem */}
          {origem && (
            <div className="transferir-pagamento__campo">
              <label className="transferir-pagamento__label">
                Parcelas a transferir
                {escolhidas.length > 0 && (
                  <span>
                    {" "}
                    — {escolhidas.length} selecionada
                    {escolhidas.length > 1 ? "s" : ""}, total{" "}
                    {formatarValor(totalSelecionado)}
                  </span>
                )}
              </label>

              <div className="transferir-pagamento__entradas">
                {origem.pagas.map((p) => (
                  <label
                    key={p._id}
                    title={
                      p.podeSerOrigem
                        ? undefined
                        : "Recebida noutra unidade. Só se transfere onde o dinheiro entrou."
                    }
                    className={`transferir-pagamento__entrada ${
                      parcelasSel.includes(p._id)
                        ? "transferir-pagamento__entrada--sel"
                        : ""
                    }`}
                  >
                    <input
                      type="checkbox"
                      value={p._id}
                      checked={parcelasSel.includes(p._id)}
                      onChange={() => alternarParcela(p._id)}
                      disabled={isLoading || !p.podeSerOrigem}
                    />
                    <span className="transferir-pagamento__entrada__desc">
                      {p.nrParcela} · {p.descricao || "(sem descrição)"}
                    </span>
                    <span className="transferir-pagamento__entrada__valor">
                      {formatarValor(p.valor)}
                    </span>
                    <span className="transferir-pagamento__entrada__data">
                      {p.dataPagamento
                        ? moment(p.dataPagamento).format("DD/MM/YYYY")
                        : "—"}
                    </span>
                    <span className="transferir-pagamento__entrada__doc">
                      {p.temRecibo
                        ? "📄 fatura + recibo"
                        : p.temDocumento
                          ? "📄 c/ fatura"
                          : "sem documento"}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* 4. Conta de destino */}
          <div className="transferir-pagamento__campo">
            <label className="transferir-pagamento__label">
              Conta de DESTINO (para onde vai o dinheiro)
            </label>
            <select
              className="transferir-pagamento__input"
              value={contaDestinoSel}
              onChange={(e) => setContaDestinoSel(e.target.value)}
              disabled={isLoading}
            >
              <option value="">— escolher —</option>
              {contas
                /* Quem decide é o servidor, no `podeSerDestino`: exclui as
                   contas por aprovar — de um orçamento desativado ou
                   substituído, cujas parcelas não contam em relatório nenhum —
                   e as de orçamento INTERROMPIDO. Ambas continuam a aparecer
                   como ORIGEM, que é onde o dinheiro fica preso. */
                .filter(
                  (c) =>
                    c._id !== contaOrigemSel &&
                    c.emAberto.length > 0 &&
                    c.podeSerDestino,
                )
                .map((c) => (
                  <option key={c._id} value={c._id}>
                    {descreverConta(c)} · em aberto{" "}
                    {formatarValor(c.totalEmAberto)}
                  </option>
                ))}
            </select>
          </div>

          {validacao && (
            <p className="transferir-pagamento__erro-validacao">{validacao}</p>
          )}

          {/* 5. Executar */}
          {!confirmando ? (
            <button
              /* Os botões usavam um modificador `--executar` que não existe no
                 estilo, e por isso saíam sem cor nenhuma. Passam a usar os
                 mesmos da ferramenta das entradas: azul para avançar, verde
                 para confirmar, cinzento para cancelar. */
              className="transferir-pagamento__btn transferir-pagamento__btn--preparar"
              onClick={() => setConfirmando(true)}
              disabled={!podeExecutar}
            >
              Transferir
            </button>
          ) : (
            <div className="transferir-pagamento__confirmacao">
              <p>
                Confirma a transferência de{" "}
                <strong>{formatarValor(totalSelecionado)}</strong> de{" "}
                <strong>{origem && descreverConta(origem)}</strong> para{" "}
                <strong>{destino && descreverConta(destino)}</strong>?
              </p>
              {/* `__btns` não tem estilo nenhum; `__acoes` é a que traz o
                  `display: flex` e o `gap` entre os dois botões. */}
              <div className="transferir-pagamento__confirmacao__acoes">
                <button
                  className="transferir-pagamento__btn transferir-pagamento__btn--confirmar"
                  onClick={handleExecutar}
                  disabled={isLoading}
                >
                  {isLoading ? "A transferir..." : "Sim, transferir"}
                </button>
                <button
                  className="transferir-pagamento__btn transferir-pagamento__btn--cancelar"
                  onClick={() => setConfirmando(false)}
                  disabled={isLoading}
                >
                  Cancelar
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {resultado && (
        <div
          className={`transferir-pagamento__resultado ${
            resultado.sucesso
              ? "transferir-pagamento__resultado--ok"
              : "transferir-pagamento__resultado--erro"
          }`}
        >
          {resultado.sucesso ? (
            <>
              <strong>Transferência concluída.</strong>
              {typeof resultado.dados?.documentosReligados === "number" && (
                <p>
                  Documentos eletrónicos reapontados:{" "}
                  {resultado.dados.documentosReligados}
                </p>
              )}
            </>
          ) : (
            <>
              <strong>Não foi possível transferir.</strong>
              <p>{resultado.mensagem}</p>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default TransferirEntreContas;
